/**
 * arbi upload <file...> [--workspace <id>] [--watch] [--dry-run] [--json]
 *
 * Uploads files, directories, or zip archives to the active workspace.
 *
 * - Directories are uploaded with folder structure preserved.
 * - Zip archives are extracted and uploaded with internal folder structure.
 * - Regular files are uploaded individually.
 * - Files are uploaded in batches of 100 per folder to avoid overwhelming the backend.
 *
 * Manifest mode (eDiscovery-grade audit trail):
 *   --manifest <file>  — read newline-delimited paths from a file instead of
 *                        positional args. Every line is treated as one input.
 *   --log <file>       — append a JSONL record per file to this path as the
 *                        upload progresses. Each record contains the doc id
 *                        (on success), a terminal status, and the reason
 *                        string for any non-success outcome. The log is
 *                        append-only and crash-safe.
 *   --resume           — read the log file, skip inputs that already have a
 *                        terminal outcome there (uploaded/duplicate/rejected),
 *                        and retry any that were marked `error`.
 *   --fail-fast        — abort the run on the first `error` outcome.
 *   --root <dir>       — root directory used to derive the backend `folder`
 *                        path for each file. Defaults to the longest common
 *                        parent of all inputs.
 *
 * Watch behaviour:
 *   --watch / -W  — always watch processing progress after upload
 *   --no-watch    — never watch, even if config watch is on
 *   (no flag)     — auto-watch in interactive terminals when config watch
 *                   is on (default); skip in pipes / CI
 *
 * When watching, failed documents show the server error reason if available.
 */

import { Command } from 'commander'
import fs from 'node:fs'
import path from 'node:path'
import { isMessageType, type WsTaskUpdateMessage } from '@arbidocs/client'
import { documentsNode, documents, connectWebSocket, DOC_TERMINAL_STATUSES } from '@arbidocs/sdk'
import { runAction, resolveWorkspace } from '../helpers.js'
import { getConfig } from '../config.js'
import { success, error, warn, status, dim } from '../output.js'
import { printSummary, type UploadOpts, type UploadSummary } from './upload-shared.js'
import { runDirectUploadMode } from './upload-direct.js'

export function registerUploadCommand(program: Command): void {
  program
    .command('add [paths...]')
    .alias('upload')
    .description('Add files, directories, or zip archives to the active workspace')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('-W, --watch', 'Watch document processing progress after upload')
    .option('--no-watch', 'Skip watching document processing')
    .option('--dry-run', 'Show what would be uploaded without uploading')
    .option('--json', 'Output results as JSON')
    .option('--config <id>', 'Config ext_id to use (e.g. cfg-xxx for SKIP_DUPLICATES)')
    .option('--manifest <file>', 'Read newline-delimited paths from file (eDiscovery mode)')
    .option('--log <file>', 'Append per-file JSONL audit log (eDiscovery mode)')
    .option('--resume', 'Skip paths already completed in --log (requires --log)')
    .option('--fail-fast', 'Abort on first upload error')
    .option('--root <dir>', 'Root directory for backend folder paths (defaults to common parent)')
    .option(
      '--limit <n>',
      'Max files to upload this run (applied after --resume filter, manifest mode only)'
    )
    .option(
      '-s, --s3',
      'Upload via the direct-to-MinIO flow (SecretBox on the client, presigned PUT)'
    )
    .option('--folder <name>', 'Backend folder for plain-file uploads (direct upload only)')
    .option(
      '--wp-type <type>',
      'Work product type: source (default), skill, memory, artifact, webpage. ' +
        'Use --wp-type skill with --folder skills/<slug> to upload a SKILL.md.'
    )
    .action((paths: string[] | undefined, opts: UploadOpts) =>
      runAction(async () => {
        const isManifestMode = Boolean(opts.manifest || opts.log || opts.resume)
        const inputPaths: string[] = paths ?? []

        if (isManifestMode) {
          if (opts.s3) {
            error('--s3 is not supported with manifest mode yet.')
            process.exit(1)
          }
          await runManifestMode(inputPaths, opts)
          return
        }

        if (opts.s3) {
          if (inputPaths.length === 0) {
            error('No paths provided.')
            process.exit(1)
          }
          await runDirectUploadMode(inputPaths, opts)
          return
        }

        if (inputPaths.length === 0) {
          error('No paths provided. Pass file/dir args or use --manifest <file>.')
          process.exit(1)
        }

        // Validate paths exist before authenticating
        for (const p of inputPaths) {
          if (!fs.existsSync(p)) {
            error(`Path not found: ${p}`)
            process.exit(1)
          }
        }

        // Determine early whether we'll watch — if so, skip background notifications
        // to avoid session conflicts (backend enforces one WS per session).
        const isInteractive = process.stdout.isTTY === true
        const watchPref = getConfig()?.watch !== false
        const willWatch =
          opts.watch === false ? false : opts.watch === true || (watchPref && isInteractive)

        const { config, accessToken } = await resolveWorkspace(opts.workspace, {
          skipNotifications: willWatch,
        })

        // Track uploaded doc IDs and their file names for --watch
        const uploadedDocs = new Map<string, string>()
        const auth = { baseUrl: config.baseUrl, accessToken }

        // Aggregate summary
        const summary: UploadSummary = {
          totalFiles: 0,
          uploaded: 0,
          skipped: 0,
          duplicates: 0,
          folders: 0,
          docIds: [],
          skippedDetails: [],
        }

        // Progress callbacks for batched uploads
        const progressCallbacks: documentsNode.UploadDirectoryOptions = {
          configExtId: opts.config,
          onBatchStart: (progress: documentsNode.UploadProgress) => {
            if (!opts.json) {
              dim(
                `  [${progress.batch}/${progress.totalBatches}] Uploading ${progress.filesInBatch} file(s) from ${progress.folder}...`
              )
            }
          },
          onBatchComplete: (
            progress: documentsNode.UploadProgress & {
              result: {
                doc_ext_ids?: string[]
                skipped?: Array<{ file_name: string; reason: string }>
              }
            }
          ) => {
            const uploaded = progress.result.doc_ext_ids?.length ?? 0
            const skipped = progress.result.skipped?.length ?? 0
            if (!opts.json) {
              success(
                `  [${progress.batch}/${progress.totalBatches}] ${uploaded} uploaded, ${skipped} skipped (${progress.filesUploaded}/${progress.totalFiles} total)`
              )
            }
          },
        }

        for (const filePath of inputPaths) {
          const stat = fs.statSync(filePath)

          if (stat.isDirectory()) {
            if (opts.dryRun) {
              // Dry run: just count files
              const count = countSupportedFiles(filePath)
              console.log(`${filePath}: ${count} supported file(s) would be uploaded`)
              summary.totalFiles += count
              continue
            }

            const result = await documentsNode.uploadDirectory(auth, filePath, progressCallbacks)

            if (result.doc_ext_ids.length === 0 && result.skipped.length === 0) {
              warn(`No supported files found in directory: ${filePath}`)
              continue
            }

            // Report per-folder results
            if (!opts.json) {
              for (const [folder, info] of result.folders) {
                success(
                  `  ${folder}: ${info.fileCount} file(s) → ${info.doc_ext_ids.length} uploaded`
                )
                if (info.skipped.length > 0) {
                  warn(
                    `    Skipped: ${info.skipped.map((s) => `${s.file_name} (${s.reason})`).join(', ')}`
                  )
                }
              }
              success(
                `Uploaded directory: ${filePath} (${result.doc_ext_ids.length} document(s) total)`
              )
            }

            updateSummary(summary, result)
            for (const id of result.doc_ext_ids) uploadedDocs.set(id, filePath)
          } else if (filePath.toLowerCase().endsWith('.zip')) {
            if (opts.dryRun) {
              console.log(`${filePath}: zip archive (contents would be extracted and uploaded)`)
              continue
            }

            const result = await documentsNode.uploadZip(auth, filePath, progressCallbacks)

            if (result.doc_ext_ids.length === 0 && result.skipped.length === 0) {
              warn(`No supported files found in zip: ${filePath}`)
              continue
            }

            if (!opts.json) {
              for (const [folder, info] of result.folders) {
                success(
                  `  ${folder}: ${info.fileCount} file(s) → ${info.doc_ext_ids.length} uploaded`
                )
                if (info.skipped.length > 0) {
                  warn(
                    `    Skipped: ${info.skipped.map((s) => `${s.file_name} (${s.reason})`).join(', ')}`
                  )
                }
              }
              success(`Uploaded zip: ${filePath} (${result.doc_ext_ids.length} document(s) total)`)
            }

            updateSummary(summary, result)
            for (const id of result.doc_ext_ids) uploadedDocs.set(id, filePath)
          } else if (
            documentsNode.ARCHIVE_EXTENSIONS.has(
              filePath.slice(filePath.lastIndexOf('.')).toLowerCase()
            )
          ) {
            if (opts.dryRun) {
              console.log(`${filePath}: archive (contents would be extracted and uploaded)`)
              continue
            }

            const result = await documentsNode.uploadArchive(auth, filePath, progressCallbacks)

            if (result.doc_ext_ids.length === 0 && result.skipped.length === 0) {
              warn(`No supported files found in archive: ${filePath}`)
              continue
            }

            if (!opts.json) {
              for (const [folder, info] of result.folders) {
                success(
                  `  ${folder}: ${info.fileCount} file(s) → ${info.doc_ext_ids.length} uploaded`
                )
                if (info.skipped.length > 0) {
                  warn(
                    `    Skipped: ${info.skipped.map((s) => `${s.file_name} (${s.reason})`).join(', ')}`
                  )
                }
              }
              success(
                `Uploaded archive: ${filePath} (${result.doc_ext_ids.length} document(s) total)`
              )
            }

            updateSummary(summary, result)
            for (const id of result.doc_ext_ids) uploadedDocs.set(id, filePath)
          } else {
            if (opts.dryRun) {
              console.log(`${filePath}: single file would be uploaded`)
              summary.totalFiles += 1
              continue
            }

            const result = await documentsNode.uploadLocalFile(auth, filePath, {
              folder: opts.folder,
              wpType: opts.wpType,
            })

            if (!opts.json) {
              success(`Uploaded: ${result.fileName} (${(result.doc_ext_ids ?? []).join(', ')})`)
              if (result.skipped && result.skipped.length > 0) {
                warn(
                  `  Skipped: ${result.skipped.map((s) => `${s.file_name} (${s.reason})`).join(', ')}`
                )
              }
            }

            summary.totalFiles += 1
            summary.uploaded += result.doc_ext_ids?.length ?? 0
            summary.docIds.push(...(result.doc_ext_ids ?? []))
            if (result.skipped) {
              summary.skipped += result.skipped.length
              summary.skippedDetails.push(...result.skipped)
            }

            for (const id of result.doc_ext_ids ?? []) uploadedDocs.set(id, result.fileName)
          }
        }

        if (opts.dryRun) {
          return
        }

        // Print or output summary
        if (opts.json) {
          console.log(JSON.stringify(summary))
        } else {
          printSummary(summary)
        }

        // Watch processing progress
        if (willWatch && uploadedDocs.size > 0) {
          const pending = new Set(uploadedDocs.keys())
          const failed = new Map<string, string>()
          console.log(`\nWatching ${pending.size} document(s)...`)

          let onDone!: () => void
          const done = new Promise<void>((r) => {
            onDone = r
          })

          const conn = await connectWebSocket({
            baseUrl: config.baseUrl,
            accessToken,
            onMessage: (msg) => {
              if (isMessageType<WsTaskUpdateMessage>(msg, 'task_update')) {
                if (!pending.has(msg.doc_ext_id)) return
                const docName = uploadedDocs.get(msg.doc_ext_id) || msg.file_name
                // Backend may include extra error fields not in the TS schema
                const extra = msg as Record<string, unknown>

                if (msg.status === 'failed') {
                  const reason =
                    (extra.error_reason as string) ||
                    (extra.status_details as string) ||
                    (extra.detail as string) ||
                    'Unknown error'
                  failed.set(msg.doc_ext_id, reason)
                  console.log(`  ${docName}: ${status(msg.status)} — ${reason}`)
                  pending.delete(msg.doc_ext_id)
                } else {
                  console.log(`  ${docName}: ${status(msg.status)} (${msg.progress}%)`)
                  // Any terminal status (completed, skipped, empty, low-content)
                  // clears the row from `pending` so the watch loop can exit. The
                  // old code only checked `completed`, so a tiny txt that hit
                  // `low-content` (a real terminal state) hung the watch loop
                  // forever.
                  if (DOC_TERMINAL_STATUSES.has(msg.status as string)) {
                    pending.delete(msg.doc_ext_id)
                  }
                }

                if (pending.size === 0) {
                  conn.close()
                }
              }
            },
            onClose: () => {
              if (failed.size > 0) {
                error(`\n${failed.size} document(s) failed to process:`)
                for (const [docId, reason] of failed) {
                  error(`  ${uploadedDocs.get(docId) || docId}: ${reason}`)
                }
              }
              if (pending.size > 0) {
                warn(`\nConnection closed. ${pending.size} document(s) still processing.`)
                dim('Run "arbi watch" to continue monitoring, or "arbi docs" to check status.')
              } else if (failed.size === 0 && uploadedDocs.size > 0) {
                success('\nAll documents processed successfully.')
              }
              onDone()
            },
          })

          await done
        } else if (uploadedDocs.size > 0 && !willWatch && !opts.json) {
          dim(
            'Tip: Use -W/--watch to monitor processing progress, or run "arbi docs" to check status.'
          )
        }
      })()
    )
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function updateSummary(summary: UploadSummary, result: documents.UploadBatchResult): void {
  let totalFiles = 0
  for (const [, info] of result.folders) {
    totalFiles += info.fileCount
    summary.folders += 1
  }
  summary.totalFiles += totalFiles
  summary.uploaded += result.doc_ext_ids.length
  summary.skipped += result.skipped.length
  summary.docIds.push(...result.doc_ext_ids)
  summary.skippedDetails.push(...result.skipped)
}

function countSupportedFiles(dirPath: string): number {
  let count = 0
  for (const entry of fs.readdirSync(dirPath, { withFileTypes: true })) {
    const fullPath = `${dirPath}/${entry.name}`
    if (entry.isDirectory()) {
      count += countSupportedFiles(fullPath)
    } else if (entry.isFile()) {
      const ext = entry.name.toLowerCase().lastIndexOf('.')
      if (ext >= 0) {
        const extStr = entry.name.slice(ext).toLowerCase()
        if (documents.SUPPORTED_EXTENSIONS.has(extStr)) {
          count++
        }
      }
    }
  }
  return count
}

// ── Manifest mode (eDiscovery audit trail) ─────────────────────────────────

/** A log line we recognize when reading an existing --log file for --resume. */
interface LogEntry {
  path: string
  status: documentsNode.FileStatus | 'pending'
}

/**
 * Read prior outcomes from a JSONL log so `--resume` can skip anything that
 * already has a terminal non-error status. Returns a map of path → last
 * status seen. Ignores malformed lines and header/footer records.
 */
function readLogState(logPath: string): Map<string, documentsNode.FileStatus> {
  const state = new Map<string, documentsNode.FileStatus>()
  if (!fs.existsSync(logPath)) return state
  const text = fs.readFileSync(logPath, 'utf8')
  for (const line of text.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#')) continue
    try {
      const rec = JSON.parse(trimmed) as Partial<LogEntry> & { event?: string }
      if (rec.event) continue // header/footer
      if (typeof rec.path === 'string' && typeof rec.status === 'string') {
        state.set(rec.path, rec.status as documentsNode.FileStatus)
      }
    } catch {
      // ignore malformed lines
    }
  }
  return state
}

/** Read a newline-delimited manifest file, stripping blank lines and comments. */
function readTextManifest(manifestPath: string): string[] {
  const text = fs.readFileSync(manifestPath, 'utf8')
  const out: string[] = []
  for (const raw of text.split('\n')) {
    const line = raw.replace(/\r$/, '').trim()
    if (!line || line.startsWith('#')) continue
    out.push(line)
  }
  return out
}

// ── CSV manifest (eDiscovery load-file style) ──────────────────────────────

/**
 * Parse a CSV text body. Handles quoted values with commas, escaped double
 * quotes ("" → "), CRLF/LF line endings, and multi-line quoted values.
 * Returns rows as string arrays; caller is responsible for picking out the
 * header.
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cur = ''
  let inQuotes = false
  let i = 0
  while (i < text.length) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cur += '"'
          i += 2
          continue
        }
        inQuotes = false
        i++
      } else {
        cur += ch
        i++
      }
    } else if (ch === '"' && cur === '') {
      inQuotes = true
      i++
    } else if (ch === ',') {
      row.push(cur)
      cur = ''
      i++
    } else if (ch === '\n' || ch === '\r') {
      row.push(cur)
      cur = ''
      rows.push(row)
      row = []
      if (ch === '\r' && text[i + 1] === '\n') i += 2
      else i++
    } else {
      cur += ch
      i++
    }
  }
  if (cur !== '' || row.length > 0) {
    row.push(cur)
    rows.push(row)
  }
  // Drop a trailing empty row that a final newline produces
  if (rows.length > 0 && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === '') {
    rows.pop()
  }
  return rows
}

function csvField(v: string): string {
  if (v.includes(',') || v.includes('"') || v.includes('\n') || v.includes('\r')) {
    return '"' + v.replace(/"/g, '""') + '"'
  }
  return v
}

function writeCsvAtomic(filePath: string, header: string[], rows: string[][]): void {
  const lines: string[] = [header.map(csvField).join(',')]
  for (const row of rows) {
    const padded = row.slice()
    while (padded.length < header.length) padded.push('')
    lines.push(padded.map(csvField).join(','))
  }
  const tmp = filePath + '.tmp'
  fs.writeFileSync(tmp, lines.join('\n') + '\n')
  fs.renameSync(tmp, filePath)
}

/**
 * A loaded CSV manifest. Keeps the full rows so we can write the file back
 * with any new doc ids applied, preserving every other column.
 *
 * Required columns: `Document Name` (or `Name`) and `Folder` (or `Path`).
 * Optional writable columns: `ARBI Doc ID`, `ARBI Status`. If not present
 * they are auto-appended to the header so the updated CSV carries results.
 */
interface CsvManifest {
  filePath: string
  header: string[]
  rows: string[][]
  nameCol: number
  folderCol: number
  docIdCol: number
  statusCol: number
  /** Absolute paths in the order the rows appear. */
  paths: string[]
  /** Map of absolute path → row index, for writing back outcomes. */
  pathToRow: Map<string, number>
}

function findColumn(header: string[], candidates: string[]): number {
  const normalized = header.map((h) => h.trim().toLowerCase())
  for (const cand of candidates) {
    const i = normalized.indexOf(cand.toLowerCase())
    if (i >= 0) return i
  }
  return -1
}

function loadCsvManifest(csvPath: string, rootDir: string | undefined): CsvManifest {
  const text = fs.readFileSync(csvPath, 'utf8')
  const all = parseCsv(text)
  if (all.length === 0) {
    error(`Empty CSV: ${csvPath}`)
    process.exit(1)
  }
  const header = all[0]
  const rows = all.slice(1)

  const nameCol = findColumn(header, ['Document Name', 'Name', 'File Name', 'Filename'])
  if (nameCol < 0) {
    error(`CSV ${csvPath} is missing a Document Name / Name column.`)
    process.exit(1)
  }
  const folderCol = findColumn(header, ['Folder', 'Path', 'Directory'])
  if (folderCol < 0) {
    error(`CSV ${csvPath} is missing a Folder / Path column.`)
    process.exit(1)
  }

  let docIdCol = findColumn(header, ['ARBI Doc ID', 'Doc ID', 'DocId'])
  let statusCol = findColumn(header, ['ARBI Status', 'Status'])
  if (docIdCol < 0) {
    header.push('ARBI Doc ID')
    docIdCol = header.length - 1
  }
  if (statusCol < 0) {
    header.push('ARBI Status')
    statusCol = header.length - 1
  }

  // Pad every row so the new columns exist even if the file was shorter.
  for (const row of rows) {
    while (row.length < header.length) row.push('')
  }

  const root = rootDir ? path.resolve(rootDir) : process.cwd()
  const paths: string[] = []
  const pathToRow = new Map<string, number>()
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const name = (row[nameCol] ?? '').trim()
    if (!name) continue
    const folder = (row[folderCol] ?? '').trim()
    const rel = folder ? path.join(folder, name) : name
    const abs = path.resolve(root, rel)
    paths.push(abs)
    pathToRow.set(abs, i)
  }

  return {
    filePath: csvPath,
    header,
    rows,
    nameCol,
    folderCol,
    docIdCol,
    statusCol,
    paths,
    pathToRow,
  }
}

// ── Direct-to-MinIO upload mode ─────────────────────────────────────────────

/**
 * Execute the upload in manifest mode. Reads inputs from --manifest or
 * expands positional args (directories → their supported files), optionally
 * filters via --resume, streams per-file JSONL outcomes to --log, and prints
 * a terminal summary.
 */
async function runManifestMode(inputPaths: string[], opts: UploadOpts): Promise<void> {
  if (opts.resume && !opts.log) {
    error('--resume requires --log <file>')
    process.exit(1)
  }

  // ── Load inputs: CSV, text manifest, and/or positional args ─────────────
  const rawPaths: string[] = []
  let csv: CsvManifest | null = null
  if (opts.manifest) {
    if (!fs.existsSync(opts.manifest)) {
      error(`Manifest not found: ${opts.manifest}`)
      process.exit(1)
    }
    const ext = path.extname(opts.manifest).toLowerCase()
    if (ext === '.csv' || ext === '.tsv') {
      csv = loadCsvManifest(opts.manifest, opts.root)
      rawPaths.push(...csv.paths)
    } else {
      rawPaths.push(...readTextManifest(opts.manifest))
    }
  }
  for (const p of inputPaths) {
    if (!fs.existsSync(p)) {
      error(`Path not found: ${p}`)
      process.exit(1)
    }
    const stat = fs.statSync(p)
    if (stat.isDirectory()) {
      rawPaths.push(...documentsNode.walkSupportedFiles(p))
    } else {
      rawPaths.push(path.resolve(p))
    }
  }

  if (rawPaths.length === 0) {
    error('Manifest is empty. Nothing to upload.')
    process.exit(1)
  }

  // ── Resume filter ───────────────────────────────────────────────────────
  let pathsToUpload = rawPaths
  const priorState = opts.log ? readLogState(opts.log) : new Map<string, documentsNode.FileStatus>()
  let resumedCount = 0
  if (opts.resume) {
    pathsToUpload = rawPaths.filter((p) => {
      const prior = priorState.get(p)
      if (
        prior === 'uploaded' ||
        prior === 'duplicate' ||
        prior === 'rejected' ||
        prior === 'skipped'
      ) {
        resumedCount++
        return false
      }
      return true
    })
  }

  // ── Limit (applied after resume filter) ─────────────────────────────────
  let limit: number | null = null
  let limitedOut = 0
  if (opts.limit !== undefined) {
    const n = Number.parseInt(opts.limit, 10)
    if (!Number.isFinite(n) || n <= 0) {
      error(`Invalid --limit: ${opts.limit}. Must be a positive integer.`)
      process.exit(1)
    }
    limit = n
    if (pathsToUpload.length > n) {
      limitedOut = pathsToUpload.length - n
      pathsToUpload = pathsToUpload.slice(0, n)
    }
  }

  const isInteractive = process.stdout.isTTY === true
  const humanOutput = !opts.json

  if (humanOutput) {
    console.log(
      `Manifest: ${rawPaths.length} file(s)` +
        (csv ? ` from ${path.basename(csv.filePath)}` : '') +
        (resumedCount ? ` (${resumedCount} already done, skipping)` : '') +
        (limit !== null && limitedOut > 0
          ? ` (limit=${limit}, ${limitedOut} deferred to next run)`
          : '')
    )
  }

  if (opts.dryRun) {
    if (opts.json) {
      console.log(
        JSON.stringify({
          total: rawPaths.length,
          resumed: resumedCount,
          toUpload: pathsToUpload.length,
        })
      )
    } else {
      console.log(`Would upload ${pathsToUpload.length} file(s)`)
    }
    return
  }

  if (pathsToUpload.length === 0) {
    if (humanOutput) success('Nothing to do — all paths already completed in log.')
    return
  }

  // ── Authenticate ────────────────────────────────────────────────────────
  // Background notifications follow the same rule as the legacy path: skip
  // them when we'll open our own watch WS (one WS per session). Use
  // `arbi config notifications off` for a persistent silence.
  const watchPref = getConfig()?.watch !== false
  const willWatch =
    opts.watch === false ? false : opts.watch === true || (watchPref && isInteractive)
  const { config, accessToken, workspaceId } = await resolveWorkspace(opts.workspace, {
    skipNotifications: willWatch,
  })
  const auth = { baseUrl: config.baseUrl, accessToken }

  // ── Open JSONL log ──────────────────────────────────────────────────────
  let logFd: number | null = null
  if (opts.log) {
    const dir = path.dirname(path.resolve(opts.log))
    fs.mkdirSync(dir, { recursive: true })
    logFd = fs.openSync(opts.log, 'a')
    const header = JSON.stringify({
      ts: new Date().toISOString(),
      event: 'run_start',
      workspace: workspaceId,
      config: opts.config ?? null,
      inputs: rawPaths.length,
      toUpload: pathsToUpload.length,
      resumed: resumedCount,
    })
    fs.writeSync(logFd, header + '\n')
    fs.fsyncSync(logFd)
  }

  const uploadedDocs = new Map<string, string>()
  const runStart = Date.now()

  const writeLog = (o: documentsNode.FileOutcome): void => {
    if (logFd === null) return
    const rec = {
      ts: new Date().toISOString(),
      path: o.path,
      folder: o.folder,
      sizeBytes: o.sizeBytes,
      status: o.status,
      ...(o.docId ? { docId: o.docId } : {}),
      ...(o.reason ? { reason: o.reason } : {}),
      attempt: o.attempt,
      ...(o.batchId ? { batchId: o.batchId } : {}),
    }
    fs.writeSync(logFd, JSON.stringify(rec) + '\n')
    fs.fsyncSync(logFd)
  }

  // ── Human-readable streaming output ─────────────────────────────────────
  // Non-error outcomes tick a compact progress counter on stderr; errors
  // and rejections print inline so they stand out.
  const total = pathsToUpload.length
  let done = 0
  let uploadedCount = 0
  let duplicateCount = 0
  let rejectedCount = 0
  let errorCount = 0
  let skippedCount = 0

  const renderProgress = (): void => {
    if (!humanOutput || !isInteractive) return
    const pct = total > 0 ? Math.floor((done / total) * 100) : 100
    const line =
      `  ${done}/${total} (${pct}%)` +
      `  ok=${uploadedCount}` +
      (duplicateCount > 0 ? ` dup=${duplicateCount}` : '') +
      (rejectedCount > 0 ? ` rej=${rejectedCount}` : '') +
      (skippedCount > 0 ? ` skip=${skippedCount}` : '') +
      (errorCount > 0 ? ` err=${errorCount}` : '')
    // \r overwrite; pad with spaces so shorter lines don't leave garbage
    process.stderr.write('\r' + line.padEnd(80, ' '))
  }

  const clearProgress = (): void => {
    if (humanOutput && isInteractive) process.stderr.write('\r' + ' '.repeat(80) + '\r')
  }

  const printInlineOutcome = (o: documentsNode.FileOutcome): void => {
    if (!humanOutput) return
    const name = path.basename(o.path)
    if (o.status === 'rejected') {
      clearProgress()
      warn(`  ✗ ${name}: ${o.reason}`)
    } else if (o.status === 'error') {
      clearProgress()
      error(`  ! ${name}: ${o.reason}`)
    }
  }

  // ── CSV writeback (checkpoint every 25 outcomes) ────────────────────────
  let sinceCheckpoint = 0
  const CHECKPOINT_EVERY = 25
  const csvStatusText = (o: documentsNode.FileOutcome): string => {
    if (o.status === 'uploaded') return 'uploaded'
    if (o.status === 'duplicate') return `duplicate: ${o.reason ?? ''}`.trim()
    if (o.status === 'rejected') return `rejected: ${o.reason ?? ''}`.trim()
    if (o.status === 'skipped') return `skipped: ${o.reason ?? ''}`.trim()
    return `error: ${o.reason ?? ''}`.trim()
  }
  const flushCsv = (): void => {
    if (csv === null) return
    writeCsvAtomic(csv.filePath, csv.header, csv.rows)
  }

  const result = await documentsNode.uploadManifest(auth, pathsToUpload, {
    rootDir: opts.root,
    configExtId: opts.config,
    failFast: opts.failFast,
    onFile: async (outcome) => {
      writeLog(outcome)
      done++
      switch (outcome.status) {
        case 'uploaded':
          uploadedCount++
          if (outcome.docId) uploadedDocs.set(outcome.docId, path.basename(outcome.path))
          break
        case 'duplicate':
          duplicateCount++
          break
        case 'rejected':
          rejectedCount++
          break
        case 'error':
          errorCount++
          break
        case 'skipped':
          skippedCount++
          break
      }
      printInlineOutcome(outcome)
      renderProgress()

      if (csv !== null) {
        const rowIdx = csv.pathToRow.get(outcome.path)
        if (rowIdx !== undefined) {
          if (outcome.docId) csv.rows[rowIdx][csv.docIdCol] = outcome.docId
          csv.rows[rowIdx][csv.statusCol] = csvStatusText(outcome)
          sinceCheckpoint++
          if (sinceCheckpoint >= CHECKPOINT_EVERY) {
            flushCsv()
            sinceCheckpoint = 0
          }
        }
      }
    },
  })

  // Final CSV flush + log footer
  if (csv !== null && sinceCheckpoint > 0) flushCsv()

  if (logFd !== null) {
    const footer = JSON.stringify({
      ts: new Date().toISOString(),
      event: 'run_end',
      ...result.summary,
      elapsedMs: Date.now() - runStart,
    })
    fs.writeSync(logFd, footer + '\n')
    fs.fsyncSync(logFd)
    fs.closeSync(logFd)
  }

  clearProgress()

  // ── Summary output ──────────────────────────────────────────────────────
  if (opts.json) {
    console.log(
      JSON.stringify({
        ...result.summary,
        elapsedMs: Date.now() - runStart,
        docIds: result.outcomes.filter((o) => o.docId).map((o) => o.docId),
      })
    )
  } else {
    const elapsed = ((Date.now() - runStart) / 1000).toFixed(1)
    console.log('')
    console.log('Upload summary')
    console.log('──────────────')
    console.log(`  Files:       ${result.summary.total}`)
    if (result.summary.uploaded > 0) success(`  Uploaded:    ${result.summary.uploaded}`)
    if (result.summary.duplicate > 0) dim(`  Duplicates:  ${result.summary.duplicate}`)
    if (result.summary.rejected > 0) warn(`  Rejected:    ${result.summary.rejected}`)
    if (result.summary.skipped > 0) dim(`  Skipped:     ${result.summary.skipped}`)
    if (result.summary.error > 0) error(`  Errors:      ${result.summary.error}`)
    console.log(`  Elapsed:     ${elapsed}s`)
    if (opts.log) dim(`  Log:         ${opts.log}`)
    if (csv) dim(`  CSV:         ${csv.filePath} (updated)`)
  }

  // Exit code: 1 if any errors, 0 otherwise
  if (result.summary.error > 0) {
    if (!opts.json) {
      dim('\nSome files errored. Re-run with --resume to retry failed entries.')
    }
    process.exit(1)
  }

  // Watch processing if requested
  if (willWatch && uploadedDocs.size > 0) {
    const pending = new Set(uploadedDocs.keys())
    const failed = new Map<string, string>()
    console.log(`\nWatching ${pending.size} document(s)...`)

    let onDone!: () => void
    const done = new Promise<void>((r) => {
      onDone = r
    })

    const conn = await connectWebSocket({
      baseUrl: config.baseUrl,
      accessToken,
      onMessage: (msg) => {
        if (isMessageType<WsTaskUpdateMessage>(msg, 'task_update')) {
          if (!pending.has(msg.doc_ext_id)) return
          const docName = uploadedDocs.get(msg.doc_ext_id) || msg.file_name
          const extra = msg as Record<string, unknown>

          if (msg.status === 'failed') {
            const reason =
              (extra.error_reason as string) ||
              (extra.status_details as string) ||
              (extra.detail as string) ||
              'Unknown error'
            failed.set(msg.doc_ext_id, reason)
            console.log(`  ${docName}: ${status(msg.status)} — ${reason}`)
            pending.delete(msg.doc_ext_id)
          } else {
            console.log(`  ${docName}: ${status(msg.status)} (${msg.progress}%)`)
            // See watch-loop fix above — any terminal status clears pending so
            // the loop exits on `skipped` / `empty` / `low-content` too.
            if (DOC_TERMINAL_STATUSES.has(msg.status as string)) {
              pending.delete(msg.doc_ext_id)
            }
          }

          if (pending.size === 0) {
            conn.close()
          }
        }
      },
      onClose: () => {
        if (failed.size > 0) {
          error(`\n${failed.size} document(s) failed to process:`)
          for (const [docId, reason] of failed) {
            error(`  ${uploadedDocs.get(docId) || docId}: ${reason}`)
          }
        }
        if (pending.size > 0) {
          warn(`\nConnection closed. ${pending.size} document(s) still processing.`)
          dim('Run "arbi watch" to continue monitoring, or "arbi docs" to check status.')
        } else if (failed.size === 0 && uploadedDocs.size > 0) {
          success('\nAll documents processed successfully.')
        }
        onDone()
      },
    })

    await done
  }
}
